import { Component, inject, OnInit } from '@angular/core'; 
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentSearchModalComponent } from '../payment-search-modal/payment-search-modal.component';
import { ReceivableDomain } from '../domain/receivable-domain';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { Account } from '@ohioepa/oepa-ebiz-lib';
import { PaymentService } from '../payment.service';
import { environment } from '../../environments/environment';
import { OepaIopAuthLibService } from '@ohioepa/oepa-iop-auth-lib';
import { ReceivableSearchService } from '../receivable-search.service';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RecaptchaModule } from "ng-recaptcha";
import packageJson from '../../../package.json';


@Component({
  selector: 'app-payment-home',
  standalone: true,
  imports: [
    MatCardModule, MatButtonModule, MatDialogModule, CommonModule, MatProgressSpinnerModule, RecaptchaModule,
    MatIconModule,
  ],
  templateUrl: './payment-home.component.html',
  styleUrl: './payment-home.component.css',
  providers: []
})
export class PaymentHomeComponent implements OnInit {
  public account: Account | undefined;
  readonly dialog = inject(MatDialog);
  public profile: any = undefined;
  public token: string = '';
  public refreshToken: string = '';
  public idToken: string = '';
  public ebizRootURL = environment.ebizRootURL;
  public isLoading: boolean = true;
  public userProfile: any;
  public showRequestPin : boolean = false;
  private _snackBar = inject(MatSnackBar);
  public showRecaptcha = true;
  public recaptchaSiteKey = environment.recaptchaSiteKey;
  public versionNumber = packageJson.version;

  constructor(public authService: OepaIopAuthLibService,
    private router: Router,
    private route: ActivatedRoute,
    private receivableSearchService: ReceivableSearchService,
    private paymentService: PaymentService) {
  }

  ngOnInit(): void {
    this.getEBizVersion();
    let userID = sessionStorage.getItem('UID');
    this.route.queryParams.subscribe(params => {
      if (userID && this.authService.hasValidAccessToken()) {
        this.authService.loadUserProfile();
        setTimeout(() => {
          this.retrieveUserInfo(userID!);
        }, 500)      
      }  else if (params['uid']) {
        const userID = params['uid'];     
        this.initialLogin(userID);         
      } 
      else if (params['code']) {
        this.login()
      }      
      //if we make it here, this should be an anonymous user.   
      else {
        sessionStorage.removeItem('UID');
        this.account = undefined;
        this.isLoading = false;
      }
    });
  }

  openSnackBar(message: string, action: string, severity: string) {
    if (severity=='info') {
      this._snackBar.open(message, action, {duration: 3200, panelClass: ['mat-toolbar', 'green-snackbar']});
    } else if (severity=='error') {
      this._snackBar.open(message, action, {duration: 6200, panelClass: ['mat-toolbar', 'red-snackbar']});
    }
  }

  initialLogin(userID: any) {
    sessionStorage.setItem('UID', userID);
    this.authService.configureOAuth();
    setTimeout(() => {
      this.authService.login();
    }, 1000)
  }

  async login() {
    this.authService.configureOAuth()
    setTimeout(async () => {
      let authenticated: Promise<boolean> = this.authService.tryLogin();
      let userID = sessionStorage.getItem('UID');
      if (userID && await authenticated) {
        //do not retrieve userInfo until we get authenticated response.
        this.retrieveUserInfo(userID);
      }
    }, 500)
  }

  //Retrieves user info from eBiz. 
  retrieveUserInfo(userID: string) {
    this.paymentService.retrieveAccountByUserID(userID).subscribe({  
      next: account => {     
        this.receivableSearchService.setAccount(account);       
          this.authService.userProfile$.subscribe((profile) => {
            this.userProfile = profile
            this.verifyUser(this.userProfile, userID, account)              
          })
      }, error: err => {
        const message = err.error.message || err.message || JSON.stringify(err);
        this.openSnackBar('ERROR retrieving account: ' + message, '', 'error');
      }
    });
  }

  //the request PIN button should only be visible if the authenticated user has no PIN.
  hidePinRequest(account: Account){
    this.showRequestPin = true;
    account.programPrivileges?.forEach((priv)=>{
      if (priv.role?.name === 'PinHolder'){
        this.showRequestPin = false;   
        return
      }
    })
  }

  /*VerifyUser matches the OHID from IOP to the eBiz userID received in the page. 
   *If these are not associated, we default to an anonymous user. 
  */ 
  verifyUser(userProfile :any, userID : string, account: Account) {
    this.paymentService.verifyUserAccount(userProfile.eidmuserid, userID).subscribe({
      next: ver =>{
      if(ver){
        this.account = account;
        this.showRecaptcha = false;
        this.hidePinRequest(account)
        this.isLoading = false;
      } else{
        this.account = undefined;
        this.receivableSearchService.setAccount(this.account);       
        this.isLoading = false;
        sessionStorage.removeItem('UID');      
        this.openSnackBar("Account verification has failed. The eBiz account and OHID account are not correlated. You are now signed in as a guest", '', 'error')
      }    
    }, error : err =>{
      this.account = undefined;
      this.receivableSearchService.setAccount(this.account);       
      this.isLoading = false;
      sessionStorage.removeItem('UID');
      this.openSnackBar("ERROR verifying account. You are now signed in as a guest" + err, '', 'error')  
     }
    });    
  }

  //Parses the 'claims' provided by IOP for the user.
  parseUserProfile() {
    let profile: any = this.authService.userProfile$;
    if (profile) {
      this.profile = profile.source._value;
    }
  }

  openSearchDialog() {
    const dialogRef = this.dialog.open(PaymentSearchModalComponent, { ariaLabelledBy: 'paymentSearchDialogTitle' });
    dialogRef.afterClosed().subscribe((receivable: ReceivableDomain) => {
      if (receivable) {
        this.router.navigate(['invoice-list'], { relativeTo: this.route.parent });
      }
    });
  }

  openPinRequest() {
    const pinURL = environment.ebizRootURL + '/app/services/account/account.html#/pinRequest';
    window.location.href = pinURL;
  }

  exit() {
    let exitURL = ''
    //If this is a test account user, we need to re-navigate back to ?uid instead of portal/home as to not prompt IOP login
    if(this.paymentService.isTestAccount() && this.ebizRootURL !== "https://ebiz.epa.ohio.gov"){
      exitURL = environment.ebizRootURL + '/?uid=' + this.account?.userID;
    }else{
      exitURL = environment.ebizRootURL + '/portal/Home.po';
    }
    this.authService.logOut();
    window.location.href = exitURL;
  }

  resolved(captchaResponse: string | null) {
    this.paymentService.validateRecaptcha(captchaResponse).subscribe(() => {
      this.showRecaptcha = false;
    });
  }

  private getEBizVersion() {
    this.paymentService.getEBizVersion().subscribe(version => {
      if (version) {
        this.versionNumber += `/${version}`;
      }
    });
  }

}
